// GoogleVerificationPanel — Feb 2026 (Randy)
//
// Sits between AdminGoogleCloudPanel (which handles the *technical* OAuth
// client + redirect URI setup so users can connect Google Calendar) and
// MigrationPanel. Its job: guide the super-admin through the SEPARATE
// paperwork/review process of getting Google to publicly VERIFY the app
// so users no longer see the "Google hasn't verified this app" scary
// screen and the platform gets lifted from the 100-user unverified cap.
//
// The Cloud Setup panel is the plumbing. This panel is the paperwork.
// They're related but distinct: verification is filed *against* the same
// OAuth client the Cloud Setup panel configured.
//
// This is deliberately not talking to the backend. Verification lives
// entirely inside the Google Cloud Console — this is a walkthrough with
// copyable scope justifications tuned to My Silo's actual scope usage.

import { useState } from "react";
import {
  ShieldCheck, ArrowSquareOut, CaretUp, CaretDown, Copy, CheckCircle,
  Warning, Clock, FilmSlate,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function Ext({ href, children }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-[#4A5D4E] hover:underline break-all">
      {children} <ArrowSquareOut size={11} weight="bold" />
    </a>
  );
}

function Step({ num, title, tag, tagTone = "info", children }) {
  const [open, setOpen] = useState(false);
  const tones = {
    info: "text-[#4A5D4E] bg-[#EAF0EB] border-[#B9D4BE]",
    warn: "text-[#8A6A24] bg-[#FDF6E5] border-[#EAD9AE]",
    critical: "text-[#8A3A24] bg-[#FBEBE4] border-[#E4B8A4]",
    done: "text-[#2F6B3A] bg-[#EAF0EB] border-[#B9D4BE]",
  };
  return (
    <div className="border rounded-lg overflow-hidden border-[#E5E4E2] bg-white"
      data-testid={`gverify-step-${num}`}>
      <button onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-[#F0F1EE]/50"
        data-testid={`gverify-step-toggle-${num}`}>
        <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium shrink-0 bg-[#F0F1EE] text-[#2C2B2A]">
          {num}
        </div>
        <div className="flex-1 text-sm font-medium text-[#1C1B1A]">{title}</div>
        {tag && (
          <span className={`hidden sm:inline-block text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded border ${tones[tagTone]}`}>
            {tag}
          </span>
        )}
        {open ? <CaretUp size={14} className="text-[#2C2B2A]" /> : <CaretDown size={14} className="text-[#2C2B2A]" />}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 text-xs text-[#1C1B1A] leading-relaxed space-y-2 border-t hairline">
          {children}
        </div>
      )}
    </div>
  );
}

function CopyBlock({ label, text, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied to clipboard.");
      setTimeout(() => setCopied(false), 1600);
    } catch { toast.error("Couldn't copy."); }
  };
  return (
    <div className="mt-2 border hairline rounded bg-[#FBFAF6]" data-testid={testId}>
      <div className="flex items-center justify-between px-2 py-1 border-b hairline bg-white/60">
        <span className="text-[9px] uppercase tracking-[0.16em] text-[#6B6A68]">{label}</span>
        <button onClick={copy}
          data-testid={`${testId}-copy-btn`}
          className="text-[10px] px-1.5 py-0.5 rounded border hairline hover:bg-[#F0F1EE] inline-flex items-center gap-1">
          {copied ? <><CheckCircle size={10} weight="fill" className="text-[#2F6B3A]" /> Copied</> : <><Copy size={10} weight="bold" /> Copy</>}
        </button>
      </div>
      <pre className="text-[11px] leading-relaxed text-[#1C1B1A] px-2 py-2 whitespace-pre-wrap font-mono">{text}</pre>
    </div>
  );
}

// Scope justification copy tuned to My Silo's actual scope usage. Kept
// under Google's ~500-char justification field limit for each scope, and
// written in the "how the scope is used to deliver a specific user-visible
// feature" framing Google's review team looks for.
const CALENDAR_JUSTIFICATION =
  "My Silo is an AI family organizer. After a user connects their Google " +
  "Calendar we read and create events on their PRIMARY calendar so parents " +
  "can see the family schedule alongside AI-generated chores/rewards, and so " +
  "chores/reminders can be pushed as calendar events. All writes require an " +
  "explicit in-app action (the AI never writes silently). Users can revoke " +
  "access from the same in-app switch that connected them, which " +
  "immediately stops all Calendar API calls for that account.";

const YT_JUSTIFICATION =
  "My Silo is a creator productivity tool. After a creator connects THEIR " +
  "OWN YouTube channel we read incoming comments on the creator's videos, " +
  "run them through an AI assistant to draft private reply suggestions, and " +
  "post the reply the creator explicitly approves as a comment on that " +
  "same video. Every AI-drafted reply is human-reviewed and human-sent — " +
  "we never auto-post. Users can revoke access from the same in-app switch " +
  "that connected them, which immediately deletes their tokens and stops " +
  "all YouTube API calls.";

const HOMEPAGE_COPY_HINT = "https://mysilo.app";
const PRIVACY_HINT = "https://mysilo.app/legal/privacy";
const TERMS_HINT = "https://mysilo.app/legal/terms";
const DEMO_VIDEO_SCRIPT = `SCRIPT — YouTube demo video (target: 90-120s, unlisted upload OK)

0:00  "This is My Silo. I'm about to show you exactly why our app needs
       the YouTube force-ssl scope."

0:05  Log in → open a public silo → tap Workbench → tap YT Replies card.

0:15  Show the connect flow: user pastes their OWN Client ID/Secret (BYOK).
       "Users bring their own OAuth credentials — we don't own the app;
       they do."

0:30  Google consent screen appears with 'youtube.force-ssl' listed.
       Approve.

0:40  Back in Workbench. A pending comment appears with an AI-drafted
       reply. "This is a human-reviewed suggestion, not an auto-post."

0:55  Edit the draft in place. Tap 'Send'. Reply appears on YouTube
       within seconds. Show the actual comment on the actual video.

1:15  Show the disconnect switch. Tap it. Show the toast: "Disconnected —
       tokens deleted." Confirm the poller stops.

1:25  Close with: "Reads: comments on THEIR OWN videos. Writes: replies
       the human explicitly approved. No automation, no bulk actions,
       no access to other creators' data. That's the whole use case."
`;

export default function GoogleVerificationPanel() {
  return (
    <div className="bg-white border hairline rounded-xl p-5" data-testid="google-verification-panel">
      <div className="flex items-start gap-3 mb-3">
        <ShieldCheck size={20} weight="duotone" className="text-[#4A5D4E] shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] font-medium">Go-Live Wizard · Submit for Google Verification</div>
          <div className="text-[15px] font-medium text-[#1C1B1A] leading-snug">
            Get Google to publicly verify My Silo
          </div>
          <div className="text-xs text-[#6B6A68] mt-1">
            Removes the &quot;Google hasn&apos;t verified this app&quot; warning for every user + lifts the 100-user unverified cap. This is <strong>paperwork</strong>, not code — you file it once, wait 2-6 weeks, and it applies to every user who connects Calendar or YouTube through My Silo. Different from <em>Admin · Google Cloud Setup</em> above (that&apos;s the technical OAuth client). File this <strong>after</strong> the Cloud Setup is green.
          </div>
        </div>
      </div>

      <div className="text-[11px] rounded-lg border hairline bg-[#FDF6E5] text-[#8A6A24] px-3 py-2 mb-3 flex items-start gap-2" data-testid="gverify-warning-banner">
        <Warning size={14} weight="duotone" className="shrink-0 mt-0.5" />
        <div>
          <strong>YouTube scopes are RESTRICTED.</strong> Google requires a scope-usage <em>video</em> for
          <span className="font-mono mx-1">youtube.force-ssl</span> and may require an independent security
          assessment if you exceed 100 users. See Step 4 for the exact demo-video script.
        </div>
      </div>

      <div className="space-y-2" data-testid="gverify-steps">
        <Step num={1} title="Prep — the four public URLs Google will check" tag="~30 min" tagTone="info">
          <div>Google&apos;s reviewer opens these in a fresh browser. Each MUST resolve, load in &lt;3s, and match the OAuth Consent Screen you filled in.</div>
          <ul className="list-disc pl-4 space-y-1">
            <li><strong>Homepage:</strong> a top-level page describing what My Silo is. <Ext href={HOMEPAGE_COPY_HINT}>{HOMEPAGE_COPY_HINT}</Ext></li>
            <li><strong>Privacy Policy:</strong> must specifically mention how Google user data is stored, retained, and deleted. <Ext href={PRIVACY_HINT}>{PRIVACY_HINT}</Ext></li>
            <li><strong>Terms of Service:</strong> user-facing terms. <Ext href={TERMS_HINT}>{TERMS_HINT}</Ext></li>
            <li><strong>Authorized domain:</strong> exactly <span className="font-mono">mysilo.app</span> — DNS record must be verified in Google Search Console under the same Google account that owns the Cloud project.</li>
          </ul>
          <div className="mt-2 text-[#6B6A68] italic">
            If Privacy/Terms aren&apos;t up yet: put a simple 1-page static file at each URL BEFORE submitting. Reviewers will fail the app if a link 404s.
          </div>
        </Step>

        <Step num={2} title="OAuth Consent Screen — publish to Production" tag="~15 min" tagTone="info">
          <div>In Google Cloud Console:</div>
          <ol className="list-decimal pl-4 space-y-1">
            <li>Open <Ext href="https://console.cloud.google.com/apis/credentials/consent">APIs & Services → OAuth consent screen</Ext></li>
            <li>User type: <strong>External</strong>.</li>
            <li>App name: <span className="font-mono">My Silo</span> · User support email: your admin email · App logo: 120×120 PNG.</li>
            <li>App domain block: fill Homepage, Privacy, Terms (from Step 1). Authorized domain: <span className="font-mono">mysilo.app</span>.</li>
            <li>Developer contact info: your admin email (Google emails you here for review updates).</li>
            <li>Tap <strong>PUBLISH APP</strong>. You&apos;ll be in &quot;Testing&quot; until you file the verification.</li>
          </ol>
        </Step>

        <Step num={3} title="Add scopes + paste scope justifications" tag="~10 min" tagTone="warn">
          <div>Still on the OAuth Consent Screen → tap <strong>Add or Remove Scopes</strong>. My Silo uses these:</div>
          <ul className="list-disc pl-4 space-y-1">
            <li><span className="font-mono">.../auth/calendar</span> — Calendar read/write (restricted)</li>
            <li><span className="font-mono">.../auth/calendar.events</span> — Calendar events (sensitive)</li>
            <li><span className="font-mono">.../auth/youtube.force-ssl</span> — YouTube comment read/write (restricted)</li>
          </ul>
          <div className="mt-2">Save. Then in the <strong>Justification</strong> textarea for each, paste the matching block below. Google&apos;s reviewer copies these into their internal ticket verbatim — keep them accurate.</div>
          <CopyBlock label="Justification — Calendar scopes" text={CALENDAR_JUSTIFICATION}
            testId="gverify-cal-justification" />
          <CopyBlock label="Justification — YouTube force-ssl scope" text={YT_JUSTIFICATION}
            testId="gverify-yt-justification" />
        </Step>

        <Step num={4} title="Record the YouTube demo video (restricted scope requirement)" tag="~45 min" tagTone="critical">
          <div className="flex items-start gap-2 mb-1">
            <FilmSlate size={14} weight="duotone" className="text-[#8A3A24] shrink-0 mt-0.5" />
            <div>
              <strong>Google requires a demo video</strong> for every restricted scope
              request. YouTube&apos;s <span className="font-mono">force-ssl</span> counts. Upload the video to
              YouTube as <em>Unlisted</em> and paste the link into the OAuth Consent Screen&apos;s
              &quot;Demo video&quot; field.
            </div>
          </div>
          <div>The reviewer wants to see, on screen: (1) the OAuth consent screen with the scope listed, (2) the exact user flow that uses the scope, (3) that no auto-writes happen without a human tap, (4) how the user disconnects. Everything below is that.</div>
          <CopyBlock label="Demo video script (90-120s, tuned to My Silo&apos;s YT Replies flow)"
            text={DEMO_VIDEO_SCRIPT} testId="gverify-video-script" />
          <div className="mt-2 text-[#6B6A68] italic">Tip: iPhone screen record + external mic + a real test video works fine. Reviewers don&apos;t need production polish — they need to see the actual flow.</div>
        </Step>

        <Step num={5} title="Submit for verification" tag="~5 min" tagTone="info">
          <ol className="list-decimal pl-4 space-y-1">
            <li>Back on the OAuth Consent Screen, tap <strong>Prepare for verification</strong> (the button appears once Steps 1-4 are complete).</li>
            <li>Confirm every field. Submit.</li>
            <li>You&apos;ll get an automated email within 24-48h with a ticket number. Save it — it&apos;s your reference for all follow-ups.</li>
          </ol>
        </Step>

        <Step num={6} title="What happens next" tag="2-6 weeks" tagTone="info">
          <div className="flex items-start gap-2 mb-1">
            <Clock size={14} weight="duotone" className="text-[#4A5D4E] shrink-0 mt-0.5" />
            <div>Typical timeline:</div>
          </div>
          <ul className="list-disc pl-4 space-y-1">
            <li><strong>Days 1-3:</strong> Auto-check for missing fields (URLs resolve, video URL loads).</li>
            <li><strong>Weeks 1-2:</strong> Human reviewer watches the demo video, may email follow-up questions. Reply within 72h or the ticket auto-closes.</li>
            <li><strong>Weeks 2-4:</strong> Decision. Either &quot;Verified&quot; (done — apply immediately across all users) or &quot;More info requested&quot; (patch and re-submit).</li>
            <li><strong>Restricted scope + &gt;100 users:</strong> Google may require an <em>independent security assessment</em> (typically $10-15k). Below 100 users you&apos;re almost always exempt via the narrow-use-case pathway. Since My Silo&apos;s YT flow is per-user-BYOK, argue narrow-use in the follow-up if asked.</li>
          </ul>
          <div className="mt-2 text-[#6B6A68] italic">While in-review the app still works for everyone — the &quot;Unverified&quot; warning stays visible, but the 100-user cap doesn&apos;t start counting until verification actually passes.</div>
        </Step>
      </div>
    </div>
  );
}
